import React, { useMemo } from 'react';

import moment from 'moment';

import Carousel from 'components/Carousel';
import withGrid from 'hocs/withGrid';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { HotelsResultResponse } from 'models/hotelsSearch';
import { getFormattedPrice } from 'utils/currency';

import HotelStars from '../../HotelsStars';
import {
  Card,
  CardHeader,
  CardContent,
  CardRow,
  HotelApprovalStatus,
  HotelTitle,
  HotelSubtitle,
  CardFooterPrice,
  CardFooterFullPrice,
  CardFooter,
  CardFooterBoardDescription,
  CardFooterTotalNights,
  CardFooterRefundable,
  CardFooterScore,
  CardFooterScoreValue,
  CardFooterScoreText,
  CardTaxesIncluded,
  CardFooterPayAtDestination,
  CardFooterPayAtDestinationText,
  CheckCircleIcon,
} from './styles';

interface Props {
  data: HotelsResultResponse;
  onClick?: () => void;
  testId?: string;
  enableControls?: boolean;
}

const CardHotel = ({ data, onClick, testId, enableControls=true }: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined, currencyCode: string) => {
    const { formatted } = getFormattedPrice({ value, currency: currencyCode, t });
    return formatted;
  };
  const total = data.priceFrom;
  const totalAlternative = data.priceFromAlternative;
  //const havePayAtDestination = data.roomRates?.[0].payAtDestination;
  const havePayAtDestination = data.payAtDestination;

  const hasBreakRulesMessage = !!data.breakRulesMessage && data.breakRulesMessage.length > 0;

  const amountDays = useMemo(() => {
    const iDate = moment(data.checkinDate, 'YYYY-MM-DD');
    const eDate = moment(data.checkoutDate, 'YYYY-MM-DD');
    return eDate.diff(iDate, 'days');
  }, [data.checkinDate, data.checkoutDate]);

  const approvalMessage = useMemo(() => {
    if (hasBreakRulesMessage) return data.breakRulesMessage ? data.breakRulesMessage[0] : '';
    if (data.isPreApproved) return t('hotelsCard.preApproved');
    return t('hotelsCard.withinPolicies');
  }, [data, hasBreakRulesMessage, t]);

  const perNightPrice = formattedPrice(total / amountDays, data.currencyCode);
  const perNightAlternativePrice = formattedPrice(totalAlternative / amountDays, data.currencyCodeAlternative);

  return (
    <Card havePayAtDestination={havePayAtDestination} data-testid={testId}/*  onClick={onClick} */>
      <CardHeader>
        <Carousel fit="cover" images={data.images} enableKeyboard={false} enableControls={enableControls} />
      </CardHeader>
      <CardContent onClick={onClick}>
        <CardRow>
          <HotelTitle>{data.hotelName}</HotelTitle>
          <HotelStars count={5} value={data.category} />
        </CardRow>
        <CardRow>
          <HotelSubtitle><b>{data.city}</b> {data.fullAddress}</HotelSubtitle>
          <HotelApprovalStatus isError={hasBreakRulesMessage}>{approvalMessage}</HotelApprovalStatus>
        </CardRow>
      </CardContent>
      {havePayAtDestination && (
      <CardFooterPayAtDestination>
        <CheckCircleIcon />
        <CardFooterPayAtDestinationText>{t('hotelsCard.payAtDestination')}</CardFooterPayAtDestinationText>
      </CardFooterPayAtDestination>
      )}
      <CardFooter>        
        <CardFooterPrice>
          <CardFooterBoardDescription>{data.rooms[0].boardDescription}</CardFooterBoardDescription>
          &nbsp;{t('hotelsCard.nightPrice', { value: perNightPrice })}
          {data.score > 0 && 
            (<CardFooterScore>
              <CardFooterScoreValue>
                {data.score}
              </CardFooterScoreValue>
              <CardFooterScoreText>
                {data.score >= 9 ? t('hotelsCard.excellent') : null }
                {data.score >= 8 && data.score < 9 ? t('hotelsCard.veryGood') : null }
                {data.score >= 7 && data.score < 8 ? t('hotelsCard.good') : null }
                {data.score >= 6 && data.score < 7 ? t('hotelsCard.acceptable') : null }
                {data.score < 6 ? t('hotelsCard.regular') : null }
              </CardFooterScoreText>            
            </CardFooterScore>)
          }
        </CardFooterPrice>
        <CardFooterFullPrice>
          <CardFooterTotalNights>
            {data.totalNightsHotel === 1
              ? t('hotelsCard.hotelNights', {
                  count: data.totalNightsHotel,
                })
              : t('hotelsCard.hotelNights_plural', {
                  count: data.totalNightsHotel,
                })},&nbsp;
              {data.rooms[0].adults === 1
              ? t('hotelsCard.hotelPax', {
                  count: data.rooms[0].adults,
                })
              : t('hotelsCard.hotelPaxes_plural', {
                  count: data.rooms[0].adults,
                })}</CardFooterTotalNights>
          {formattedPrice(total, data.currencyCode)}
          {data.hasVAT ? <CardTaxesIncluded>{t('hotelsCard.taxesIncluded')}</CardTaxesIncluded> : <CardTaxesIncluded>{t('hotelsCard.taxesNotIncluded')}</CardTaxesIncluded>}
          <CardFooterRefundable isRefundable={data.roomRates[0].refundableType === 'refundable'}>
            {data.roomRates[0].refundableType === 'refundable' ? t('hotelsCard.refundable') : t('hotelsCard.nonRefundable')}
          </CardFooterRefundable>
        </CardFooterFullPrice>          
      </CardFooter>
      {totalAlternative > 0 && (
        <CardFooter>
          <CardFooterPrice>{t('hotelsCard.nightPrice', { value: perNightAlternativePrice })}</CardFooterPrice>
          <CardFooterFullPrice>{formattedPrice(totalAlternative, data.currencyCodeAlternative)}</CardFooterFullPrice>
        </CardFooter>
      )}
    </Card>
  );
};

export default withGrid(CardHotel);
